// Modo offline do painel Admin: cache de leituras (GET) + fila de lançamentos.
const DB_NAME = "amc-offline";
const MAX_PENDING_UPLOADS = 10;

let _dbp = null;
const db = () => {
  if (!_dbp) {
    _dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore("cache");
        req.result.createObjectStore("queue", { keyPath: "id" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return _dbp;
};
const tx = async (store, mode, fn) => {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const r = fn(t.objectStore(store));
    t.oncomplete = () => resolve(r && "result" in r ? r.result : undefined);
    t.onerror = () => reject(t.error);
  });
};
export const cacheGet = (k) => tx("cache", "readonly", (s) => s.get(k));
export const cacheSet = (k, v) => tx("cache", "readwrite", (s) => s.put(v, k));
const queueAll = async () => ((await tx("queue", "readonly", (s) => s.getAll())) || []).sort((a, b) => a.seq - b.seq);
const queuePut = (job) => tx("queue", "readwrite", (s) => s.put(job));
export const queueRemove = async (id) => { await tx("queue", "readwrite", (s) => s.delete(id)); await refreshState(); };

// ---------- estado (pub/sub) ----------
const state = { offline: typeof navigator !== "undefined" ? !navigator.onLine : false, cachedAt: null, pending: [], syncing: false };
const subs = new Set();
const emit = () => subs.forEach((fn) => fn({ ...state }));
export const subscribeOffline = (fn) => { subs.add(fn); fn({ ...state }); return () => subs.delete(fn); };
export const setOffline = (v, cachedAt) => {
  if (state.offline !== v || (cachedAt && cachedAt !== state.cachedAt)) {
    state.offline = v;
    if (cachedAt) state.cachedAt = cachedAt;
    if (!v) state.cachedAt = null;
    emit();
  }
};
export const refreshState = async () => { state.pending = await queueAll(); emit(); };

// ---------- regras ----------
export const isAdminContext = () => !window.location.pathname.startsWith("/escola");
export const cacheKey = (cfg) => {
  const p = cfg.params ? new URLSearchParams(Object.entries(cfg.params).filter(([, v]) => v !== undefined && v !== null)).toString() : "";
  return `${cfg.url}${p ? (cfg.url.includes("?") ? "&" : "?") + p : ""}`;
};
const RULES = [
  { m: "post", re: /^\/expenses$/, label: "Gasto" },
  { m: "post", re: /^\/budgets\/[^/]+\/items\/[^/]+\/complete-toggle$/, label: "Concluir/reabrir item" },
  { m: "patch", re: /^\/budgets\/[^/]+\/items\/[^/]+\/completed-at$/, label: "Data de conclusão do item" },
  { m: "patch", re: /^\/budgets\/[^/]+\/items\/[^/]+\/photo$/, label: "Foto do item" },
  { m: "post", re: /^\/upload$/, label: "Envio de foto" },
  { m: "post", re: /^\/agenda$/, label: "Novo item na agenda" },
  { m: "patch", re: /^\/agenda\/[^/]+$/, label: "Agenda (status/edição)" },
  { m: "post", re: /^\/notes$/, label: "Nova anotação" },
  { m: "put", re: /^\/notes\/[^/]+$/, label: "Editar anotação" },
];
export const matchRule = (cfg) => RULES.find((r) => r.m === (cfg.method || "get").toLowerCase() && r.re.test((cfg.url || "").split("?")[0]));
export const isNetworkError = (err) => !err?.response && (err?.code === "ERR_NETWORK" || err?.message === "Network Error" || !navigator.onLine);

const tmpId = () => `offline-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const serializeData = async (data) => {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const entries = [];
    for (const [k, v] of data.entries()) entries.push(v instanceof Blob ? { k, blob: v, name: v.name || "foto.jpg" } : { k, v });
    return { form: entries };
  }
  if (typeof data === "string") { try { return { json: JSON.parse(data) }; } catch { return { raw: data }; } }
  return { json: data ?? null };
};

// Cria a resposta otimista e atualiza o cache para a tela refletir o lan\u00e7amento.
const optimistic = async (job, body) => {
  const url = job.url;
  let m = url.match(/^\/budgets\/([^/]+)\/items\/([^/]+)\/(complete-toggle|completed-at|photo)$/);
  if (m) {
    const key = `/budgets/${m[1]}`;
    const c = await cacheGet(key);
    const b = c?.data ? JSON.parse(JSON.stringify(c.data)) : null;
    const it = b?.items?.find((i) => i.id === m[2]);
    if (it && m[3] === "complete-toggle") { it.is_completed = !it.is_completed; it.completed_at = it.is_completed ? (body?.completed_at || "") : ""; }
    if (it && m[3] === "completed-at") it.completed_at = body?.completed_at || it.completed_at;
    if (b) await cacheSet(key, { data: b, savedAt: c.savedAt });
    return m[3] === "photo" ? { ok: true, item: it || null, offline: true } : (b || {});
  }
  if (url === "/upload") return { path: job.tmp, offline: true };
  const doc = { ...(body || {}), id: job.tmp, _offline: true, created_at: new Date().toISOString() };
  if (url === "/expenses" && body?.budget_id) {
    const key = cacheKey({ url: "/expenses", params: { budget_id: body.budget_id } });
    const c = await cacheGet(key);
    if (Array.isArray(c?.data)) await cacheSet(key, { data: [doc, ...c.data], savedAt: c.savedAt });
  }
  return doc;
};

export const enqueue = async (cfg, rule) => {
  const pending = await queueAll();
  if (rule.re.source.includes("upload") && pending.filter((j) => j.url === "/upload").length >= MAX_PENDING_UPLOADS) {
    const e = new Error("offline-limit");
    e.response = { status: 507, data: { detail: `Limite de ${MAX_PENDING_UPLOADS} fotos pendentes offline. Conecte-se para enviar.` } };
    throw e;
  }
  const payload = await serializeData(cfg.data);
  const job = {
    id: tmpId(), tmp: tmpId(), seq: Date.now() + Math.random(), method: cfg.method, url: cfg.url.split("?")[0],
    params: cfg.params || null, payload, label: rule.label, created_at: new Date().toISOString(), error: null,
  };
  await queuePut(job);
  await refreshState();
  return optimistic(job, payload.json);
};

// ---------- sincroniza\u00e7\u00e3o ----------
const substitute = (value, map) => {
  if (!map.size || value == null) return value;
  let s = JSON.stringify(value);
  map.forEach((real, tmp) => { s = s.split(tmp).join(real); });
  return JSON.parse(s);
};

export const syncQueue = async (send) => {
  if (state.syncing || !navigator.onLine) return;
  state.syncing = true;
  const jobs = await queueAll();
  if (!jobs.length) { state.syncing = false; return; }
  emit();
  const map = new Map();
  try {
    for (const j of jobs) {
      if (j.error) continue;
      let data;
      if (j.payload.form) {
        data = new FormData();
        j.payload.form.forEach((e) => (e.blob ? data.append(e.k, e.blob, e.name) : data.append(e.k, e.v)));
      } else data = substitute(j.payload.json, map);
      const url = substitute(j.url, map);
      try {
        const r = await send({ method: j.method, url, data, params: j.params });
        const real = j.url === "/upload" ? r.data?.path : r.data?.id;
        if (real) map.set(j.tmp, real);
        await tx("queue", "readwrite", (s) => s.delete(j.id));
      } catch (err) {
        if (isNetworkError(err)) break;
        await queuePut({ ...j, error: err?.response?.data?.detail || "Erro ao enviar" });
      }
    }
  } finally {
    state.syncing = false;
    await refreshState();
  }
};
